import React, { useCallback, useRef } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { batch, useDispatch } from 'react-redux';
import { IProject, IWorkProfile } from '../../types/global/helper';
import { PhotoGrid } from './PhotoGrid';
import { AudioReelGrid } from './AudioReelGrid';
import { useThemeContext } from '../../hook/useThemeManager';
import { DemoReelGrid } from './DemoReelGrid';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { setmedia } from '../../redux/project/actions';
import { setMute } from '../../redux/player/action';
import { setVideoViewCount } from '../../redux/adverts/action';
import { useAuth } from '../../module/AuthContext';
import { getIdForIbjects } from '../../utils/AppUtils';

export interface IWorkProfileProps {
    workProfile?: IWorkProfile;
    /** When set, overrides auto-detect (e.g. `WorkProfileWrapper` passes `!!external`). */
    visitorView?: boolean;
}

const sectionSubtitleSx = (themeStyle: 'dark' | 'light') => ({
    fontSize: 14,
    fontWeight: 500,
    fontFamily: FONT_ENUM.MAIN_REGULAR,
    lineHeight: 1.5,
    color: themeStyle === 'dark' ? 'rgba(255,255,255,0.55)' : 'rgba(17, 24, 39, 0.65)',
    mt: 0.5,
});

const sectionTitleSx = (themeStyle: 'dark' | 'light') => ({
    fontSize: { xs: 24, md: 30 },
    fontWeight: 700,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    lineHeight: 1.2,
    color: theme[themeStyle].primary_white,
});

const Media: React.FC<IWorkProfileProps> = ({ workProfile, visitorView: visitorViewProp }) => {
    const { themeStyle } = useThemeContext();
    const { isSignedIn, user } = useAuth();
    const dispatch = useDispatch();
    const viewCountUpdatedVideos = useRef<Set<string>>(new Set());

    const photos = workProfile?.photos ?? [];
    const demoReel = workProfile?.demo_reel ?? [];
    const audioReel = workProfile?.audio_reel ?? [];

    const reelVisitorView =
        visitorViewProp !== undefined
            ? visitorViewProp
            : !workProfile || !isSignedIn || getIdForIbjects(workProfile.user) !== user?._id;

    const bumpDemoReelViewCount = useCallback(
        (project: IProject) => {
            const videoId = project._id || '';
            if (viewCountUpdatedVideos.current.has(videoId)) {
                return;
            }
            viewCountUpdatedVideos.current.add(videoId);
            setTimeout(() => {
                dispatch(setVideoViewCount(1));
            }, 300);
        },
        [dispatch],
    );

    const openActPlayerForProject = useCallback(
        (project: IProject) => {
            dispatch(
                setmedia({
                    media: project,
                    mediaType: 'act',
                }),
            );
            batch(() => {
                dispatch(setMute(false));
            });
            bumpDemoReelViewCount(project);
        },
        [dispatch, bumpDemoReelViewCount],
    );

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '100%',
                py: { xs: 1, sm: 2 },
                px: { xs: 2, sm: 3 },
            }}>
            <Stack spacing={{ xs: 6, md: 8 }} sx={{ width: '100%', alignItems: 'stretch' }}>
                <Box component="section" sx={{ width: '100%' }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={sectionTitleSx(themeStyle)}>Portfolio photos</Typography>
                        <Typography sx={sectionSubtitleSx(themeStyle)}>
                            Selected headshots and stills from recent productions.
                        </Typography>
                    </Box>
                    <PhotoGrid
                        photos={photos}
                        onAddPhoto={() => {}}
                        layoutVariant="portfolio"
                        sx={{
                            maxWidth: '100%',
                            mb: 0,
                        }}
                    />
                </Box>

                <Box component="section" sx={{ width: '100%' }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={sectionTitleSx(themeStyle)}>Demo reel</Typography>
                        <Typography sx={sectionSubtitleSx(themeStyle)}>
                            Performance highlights and featured clips.
                        </Typography>
                    </Box>
                    <DemoReelGrid
                        themeStyle={themeStyle}
                        projects={demoReel}
                        title="Demo reel"
                        omitSectionTitle
                        flushLayout
                        visitorView={reelVisitorView}
                        onExpandToPlayer={openActPlayerForProject}
                        onInlinePlaybackStart={bumpDemoReelViewCount}
                    />
                </Box>

                <Box component="section" sx={{ width: '100%' }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography sx={sectionTitleSx(themeStyle)}>Audio reel</Typography>
                        <Typography sx={sectionSubtitleSx(themeStyle)}>
                            Voiceover samples, podcast features, and music collaborations.
                        </Typography>
                    </Box>
                    <AudioReelGrid
                        sounds={audioReel}
                        themeStyle={themeStyle}
                        omitSectionTitle
                        flushLayout
                        visitorView={reelVisitorView}
                    />
                </Box>
            </Stack>
        </Box>
    );
};

export { Media };
